<template>
  <div class="field">
    <field-label v-if="label" v-bind="{ required, requiredClass, requiredText, hint, hintIcon, hintClass, description }">
      <slot/>
    </field-label>
    <p :class="['field-description', descriptionClass]" v-if="description">{{ description }}</p>
    <div class="field is-marginless" :class="{ 'has-addons': $scopedSlots.left || $scopedSlots.right }">
      <div class="control" v-if="$scopedSlots.left"><slot name="left"/></div>
      <div class="control is-expanded" :class="{ 'has-icons-left': leftIcon, 'has-icons-right': rightIcon }">
        <input
          :class="[{ [errorClass]: hasError }, classes]"
          autocomplete="off"
          role="presentation"
          v-bind="{ disabled, id, name, placeholder, readonly }"
          @blur="blur"
          @input="input"
          @focus="focus"
          @keydown="keydown"
          class="input"
          ref="control"
          type="tel"
          v-model="amount">
        <icon v-if="leftIcon" :class="['is-left', iconClass]" :pack="leftIconPack" :type="leftIconType" :icon="leftIcon"/>
        <icon v-if="rightIcon" :class="['is-right', iconClass]" :pack="rightIconPack" :type="rightIconType" :icon="rightIcon"/>
      </div>
      <div class="control" v-if="$scopedSlots.right"><slot name="right"/></div>
    </div>
    <field-error v-if="hasError" v-bind="{ error, errorClass, errorSymbol }"/>
  </div>
</template>

<script>
import * as props from '../mixins/props'
import * as computed from '../mixins/computed'
import accounting from 'accounting-big'
import debounce from 'lodash/debounce'

export default {

  mixins: [
    props.bounds,
    props.classes,
    props.debouncable,
    props.describable,
    props.disabled,
    props.error,
    props.hintable,
    props.iconable,
    props.id,
    props.label,
    props.name,
    props.placeholder,
    props.precision,
    props.readonly,
    props.required,

    computed.hasError,
  ],

  props: {
    value: {
      type: Number|String,
      default: '',
    },
    symbol: {
      type: String,
      default: '',
    },
    symbolSuffixed: {
      type: Boolean,
      default: false
    },
    symbolSeparator: {
      type: String,
      default: ' ',
    },
    allowNegative: {
      type: Boolean,
      default: false,
    },
    convertPositive: {
      type: Boolean,
      default: false,
    },
    onlyNegative: {
      type: Boolean,
      default: false
    },
    convertNegative: {
      type: Boolean,
      default: false,
    },
    emptyValue: {
      type: Number|String,
      default: '',
    },
    precisionSeparator: {
      type: String,
      default: '.',
    },
    thousandsSeparator: {
      type: String,
      default: ',',
    },
    modelIsNumber: {
      type: Boolean,
      default: true,
    },
    trim: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      amount: '',
      emitDebounce: debounce(this.emit, this.debounce, this.debounceOptions),
    }
  },

  computed: {
    unformattedAmount() {
      return this.unformat(this.amount)
    },
    unformattedValue() {
      return this.unformat(this.value)
    },
    formatString() {
      if (!this.symbol) {
        return '%v'
      }
      return this.symbolSuffixed
        ? `%v${this.symbolSeparator}%s`
        : `%s${this.symbolSeparator}%v`
    },
  },

  watch: {
    unformattedValue(value) {
      if (this.$refs.control !== document.activeElement) {
        this.amount = this.format(value)
      }
    },
    precisionSeparator() {
      this.processAndFormat()
    },
    thousandsSeparator() {
      this.processAndFormat()
    },
    symbol() {
      this.processAndFormat()
    },
    precision() {
      this.processAndFormat()
    },
  },

  created() {
    if (this.value) {
      this.processAndFormat()
    }
  },

  methods: {
    processUnformattedValue() {
      this.processNumber(this.unformattedValue)
    },

    processUnformattedAmount() {
      this.processNumber(this.unformattedAmount)
    },

    formatAmount() {
      this.amount = this.format(this.unformattedValue)
    },

    processAndFormat() {
      this.processUnformattedValue()
      this.formatAmount()
    },

    blur(event) {
      this.$emit('blur', event)
      this.formatAmount()
    },

    focus(event) {
      this.$emit('focus', event)
      let amount = this.unformattedValue === 0
        ? null
        : accounting.formatMoney(this.unformattedValue, {
          symbol: '',
          format: '%v',
          thousand: '',
          decimal: this.precisionSeparator,
          precision: this.precision,
        })
      if (amount) {
        amount = this.trim
          ? this.trimZeros(amount, this.precisionSeparator)
          : amount
      }
      this.amount = amount
    },

    input() {
      this.processNumber(this.unformattedAmount)
    },

    keydown(event) {
      if (/^(Comma)$/.test(event.code)) {
        event.preventDefault()
      }
    },

    processNumber(value) {
      if (value >= this.max) {
        this.updateValue(this.max)
      }
      if (value <= this.min) {
        this.updateValue(this.min)
      }
      if (value > this.min && value < this.max) {
        this.updateValue(value)
      }
      if (!this.allowNegative && value < 0) {
        if (this.convertPositive) {
          this.updateValue(Math.abs(value))
        } else {
          this.updateValue(this.min >= 0 ? this.min : 0)
        }
      }
      if (this.allowNegative && this.onlyNegative && value > 0) {
        this.updateValue(this.convertNegative ? -value : 0)
      }
    },

    updateValue(value) {
      let fixedValue = accounting.toFixed(value, this.precision),
          output = this.modelIsNumber ? Number(fixedValue) : String(fixedValue),
          method = this.debounce ? 'emitDebounce' : 'emit'
      this[method](output)
    },

    trimZeros(str, decimal) {
      let parts = str.split(decimal),
          lhs = parts[0],
          rhs = parts[1].replace(/0+$/, '')
      return (rhs.length > 0)
        ? lhs + decimal + rhs
        : lhs
    },

    format(value) {
      let formatted = accounting.formatMoney(value, {
        symbol: this.symbol,
        format: this.formatString,
        precision: this.precision,
        decimal: this.precisionSeparator,
        thousand: this.thousandsSeparator,
      })

      return this.trim
        ? this.trimZeros(formatted, this.precisionSeparator)
        : formatted
    },

    unformat(value) {
      const toUnformat = typeof value === 'string' && value === '' ? this.emptyValue : value
      return accounting.unformat(toUnformat, this.precisionSeparator)
    },

    emit(value) {
      this.$emit('input', value)
    }
  }

}
</script>
